import classNames from 'classnames';
import React, { useState, useRef, useEffect, } from 'react';
import { states } from '../utils/form';
var DirectionKey;
(function (DirectionKey) {
    DirectionKey["Down"] = "ARROWDOWN";
    DirectionKey["Up"] = "ARROWUP";
})(DirectionKey || (DirectionKey = {}));
function setValue(input, value) {
    const nativeValueSetter = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value')
        .set;
    nativeValueSetter.call(input, value);
    input.dispatchEvent(new Event('input', { bubbles: true }));
}
/**
 * Allows you to apply autocomplete functionality to any <b>&lt;input /&gt;</b> (or any component that contains <b>&lt;input /&gt;</b>)
 */
export function TxAutocomplete({ items, children }) {
    const [filteredItems, setFilteredItems] = useState([]);
    const [isOpen, setIsOpen] = useState(false);
    const [focusedIndex, setFocusedIndex] = useState(null);
    const [input, setInput] = useState(null);
    const dropDownRef = useRef(null);
    const prevItemsRef = useRef();
    useEffect(() => {
        const isSame = JSON.stringify(prevItemsRef.current) === JSON.stringify(items);
        if (!isSame) {
            setFilteredItems(items);
            prevItemsRef.current = items;
        }
    }, [items]);
    useEffect(() => {
        setFocusedIndex(null);
    }, [isOpen]);
    useEffect(() => {
        const childInput = dropDownRef.current.querySelector('input');
        if (!childInput) {
            throw new Error('TxAutocomplete requires an html input to be passed as a child');
        }
        setInput(childInput);
    }, [dropDownRef]);
    function onChange(e) {
        const inputValue = e.target.value.toLowerCase();
        // Filter items that match either by state name or state abbreviation
        const filtered = items.filter((item) => {
            const matchesName = item.toLowerCase().startsWith(inputValue);
            const stateByLabel = states.find((state) => state.label.toLowerCase() === item.toLowerCase());
            const matchesCode = stateByLabel && stateByLabel.value.toLowerCase().startsWith(inputValue);
            return matchesName || matchesCode;
        });
        setFilteredItems(filtered);
        setIsOpen(true);
    }
    function onBlur(_) {
        setTimeout(() => {
            // This needs to be in a timeout so that it runs as part of the next loop.
            // the active element will not be set until after all of the focus and blur events are done
            if (!dropDownRef.current.contains(document.activeElement)) {
                const inputValue = input.value.toLowerCase();
                // Check if input matches a state name (case-insensitive)
                const matchingStateName = items.find((item) => item.toLowerCase() === inputValue);
                // Check if input matches a state abbreviation (e.g., "TX" -> "Texas")
                const matchingStateByCode = states.find((state) => state.value.toLowerCase() === inputValue);
                if (matchingStateName) {
                    // Input matches a full state name, normalize capitalization if needed
                    if (input.value !== matchingStateName) {
                        setValue(input, matchingStateName);
                    }
                }
                else if (matchingStateByCode) {
                    // Input matches a state abbreviation, replace with full name
                    setValue(input, matchingStateByCode.label);
                }
                else {
                    // No match found, clear the field
                    setValue(input, '');
                }
                setFilteredItems(items);
                setIsOpen(false);
            }
        }, 0);
    }
    function onFocus() {
        setIsOpen(true);
    }
    function onKeyDown(e) {
        const key = e.key.toUpperCase();
        if (Object.values(DirectionKey).includes(key)) {
            e.preventDefault();
            const buttons = dropDownRef.current.querySelectorAll('button');
            switch (key) {
                case DirectionKey.Down:
                    if (focusedIndex === null) {
                        updateListFocus(buttons, 0);
                    }
                    else {
                        updateListFocus(buttons, focusedIndex + 1);
                    }
                    break;
                case DirectionKey.Up:
                    if (focusedIndex !== null && focusedIndex > 0) {
                        updateListFocus(buttons, focusedIndex - 1);
                    }
                    break;
            }
        }
    }
    function onItemSelect(item) {
        setValue(input, item);
        setTimeout(() => {
            input.focus();
            setIsOpen(false);
        }, 0);
    }
    function updateListFocus(buttons, index) {
        const button = buttons[index];
        if (button) {
            button.focus();
            setFocusedIndex(index);
        }
    }
    return (React.createElement("div", { className: classNames('tx-autocomplete', { 'tx-autocomplete--active': isOpen }), ref: dropDownRef, onBlur: onBlur, onChange: onChange, onFocus: onFocus, onKeyDown: onKeyDown },
        children,
        isOpen && React.createElement(TxAutocompleteList, { items: filteredItems, onItemSelect: onItemSelect })));
}
export function TxAutocompleteList({ items, onItemSelect }) {
    return (React.createElement("ul", { className: "tx-autocomplete__list" }, items.map((item) => {
        return (React.createElement("li", { className: "tx-autocomplete__list__item", key: item },
            React.createElement("button", { type: "button", tabIndex: -1, onMouseDown: () => onItemSelect(item), onKeyDown: (e) => {
                    if (e.key.toUpperCase() === 'ENTER') {
                        onItemSelect(item);
                    }
                } }, item)));
    })));
}
//# sourceMappingURL=Autocomplete.js.map